import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ICommonState, vistaColorCode } from "../constant";
import vistaLogo from "../Images/vistalogo.png";
import slideImage1 from "../Images/sliding-images/1.jpg";
import slideImage4 from "../Images/sliding-images/4.jpg";
import slideImage7 from "../Images/sliding-images/7.jpg";
import slideImage9 from "../Images/sliding-images/9.jpg";
import slideImage16 from "../Images/sliding-images/16.jpg";
import slideImage19 from "../Images/sliding-images/19.jpg";
import slideImage33 from "../Images/sliding-images/33.jpg";
import slideImage36 from "../Images/sliding-images/36.jpg";

const slidingImages = [
  slideImage1,
  slideImage4,
  slideImage7,
  slideImage9,
  slideImage16,
  slideImage19,
  slideImage33,
  slideImage36,
];

const heroMetrics = [
  { value: "300+", label: "Spaces Designed" },
  { value: "45 Days", label: "Average Delivery" },
  { value: "Hyderabad", label: "Local Studio" },
];

type HeroVariant = "editorial-split" | "cinematic-overlay";

// Hero style switch:
// - "editorial-split": previous premium split-card design
// - "cinematic-overlay": new alternative full-bleed hero design
const HERO_VARIANT_DEFAULT: HeroVariant = "cinematic-overlay";
const SHOW_GALLERY = false;

const Navbar: React.FC<ICommonState> = (props) => {
  const { setIsModalOpen } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [heroVariant] = useState<HeroVariant>(HERO_VARIANT_DEFAULT);
  const homeRef = useRef<HTMLDivElement | null>(null);

  const navItems = [
    { text: "HOME", id: "home" },
    { text: "SERVICES", id: "service-section" },
    { text: "ABOUT US", id: "about-us-section" },
    { text: "OUR PROCESS", id: "our-process-section" },
    ...(SHOW_GALLERY ? [{ text: "GALLERY", id: "gallery-section" }] : []),
    { text: "CONTACT US", id: "contact-us-section" },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleScrollDown = () => {
    document.getElementById("service-section")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleScrollToGallery = () => {
    document.getElementById("gallery-section")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setIsModalOpen(true);
    }, 27000);

    return () => {
      clearTimeout(timeOut);
    };
  }, [setIsModalOpen]);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 24);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (slidingImages.length <= 1) return;

    const timer = window.setInterval(() => {
      setActiveSlide(
        (currentSlide) => (currentSlide + 1) % slidingImages.length,
      );
    }, 4200);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <Box
      ref={homeRef}
      sx={{
        position: "relative",
        zIndex: (theme) => theme.zIndex.drawer + 30,
      }}
    >
      {/* Navbar */}
      <AppBar
        position="fixed"
        sx={{
          background: scrolled
            ? "rgba(255, 251, 248, 0.96)"
            : "rgba(255, 251, 248, 0.9)",
          transition:
            "background 0.3s ease-in-out, box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out",
          boxShadow: scrolled
            ? "0 10px 28px rgba(20, 10, 12, 0.15)"
            : "0 8px 18px rgba(20, 10, 12, 0.1)",
          borderBottom: scrolled
            ? "1px solid rgba(128,51,65,0.2)"
            : "1px solid rgba(128,51,65,0.14)",
          backdropFilter: "blur(14px)",
          zIndex: (theme) => theme.zIndex.drawer + 20,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            height: "80px",
          }}
          id="home-section"
        >
          {/* Logo & Name */}
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => {
              homeRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }}
          >
            <img
              src={vistaLogo}
              alt="Vista Fusion Studio logo"
              aria-label="Vista Fusion Studio logo"
              style={{ width: 50, cursor: "pointer" }}
            />
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                color: vistaColorCode.vistDarkRedColour,
                ml: 2,
                fontFamily: '"Manrope", sans-serif',
                fontSize: { xs: "1.1rem", sm: "1.4rem", md: "1.6rem" },
              }}
            >
              Vista Fusion Studio
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
            {navItems.map((item) => (
              <Button
                key={item.text}
                sx={{
                  fontSize: "1rem",
                  textTransform: "none",
                  transition: "color 0.3s ease, border-bottom 0.3s ease",
                  color: vistaColorCode.vistDarkRedColour,
                  fontWeight: "bold",
                  ":hover": {
                    borderBottom: `2px solid ${vistaColorCode.vistDarkRedColour}`,
                    borderRadius: "0px",
                  },
                }}
                onClick={() => {
                  if (item.id === "home") {
                    homeRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  } else {
                    document.getElementById(item.id)?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            color="inherit"
            edge="end"
            onClick={handleDrawerToggle}
            sx={{
              display: { md: "none" },
              color: vistaColorCode.vistDarkRedColour,
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ "& .MuiDrawer-paper": { width: 250 } }}
      >
        <List>
          {navItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => {
                  if (item.id === "home") {
                    homeRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  } else {
                    document.getElementById(item.id)?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                  handleDrawerToggle();
                }}
              >
                <ListItemText
                  primary={item.text}
                  sx={{
                    color: vistaColorCode.vistDarkRedColour,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Landing Section */}
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          pt: { xs: "80px", md: "96px" },
          pb: 0,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
        }}
      >
        <SlidingImageHero activeSlide={activeSlide} />

        <Box
          sx={{
            position: "relative",
            width: "100%",
            zIndex: 2,
            px: 0,
          }}
        >
          {heroVariant === "editorial-split" ? (
            <HeroEditorialSplit
              activeSlide={activeSlide}
              onOpenInquiry={() => setIsModalOpen(true)}
              onScrollToGallery={handleScrollToGallery}
            />
          ) : (
            <HeroCinematicOverlay
              activeSlide={activeSlide}
              onOpenInquiry={() => setIsModalOpen(true)}
              onScrollToGallery={handleScrollToGallery}
            />
          )}
        </Box>
        <Button
          aria-label="Scroll down to services"
          onClick={handleScrollDown}
          sx={{
            position: "absolute",
            left: "50%",
            bottom: { xs: 18, md: 20 },
            transform: "translateX(-50%)",
            zIndex: 2,
            minWidth: "auto",
            px: 1.5,
            py: 0.7,
            color: "white",
            textTransform: "none",
            display: { xs: "none", sm: "flex" },
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            borderRadius: "999px",
            backgroundColor: "rgba(20, 9, 12, 0.38)",
            border: "1px solid rgba(255,255,255,0.28)",
            backdropFilter: "blur(4px)",
            opacity: scrolled ? 0 : 1,
            pointerEvents: scrolled ? "none" : "auto",
            transition:
              "opacity 280ms ease-in-out, background-color 220ms ease",
            "@keyframes arrowBounce": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(5px)" },
            },
            "&:hover": {
              backgroundColor: "rgba(8, 6, 8, 0.48)",
            },
          }}
        >
          <Box
            sx={{
              width: 26,
              height: 26,
              border: "1.5px solid rgba(255,255,255,0.82)",
              borderRadius: "999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.08)",
              backdropFilter: "blur(2px)",
            }}
          >
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 18,
                color: "rgba(255,255,255,0.95)",
                animation: "arrowBounce 1.3s ease-in-out infinite",
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontFamily: '"Manrope", sans-serif',
              letterSpacing: "0.12em",
              fontWeight: 700,
              color: "rgba(255,255,255,0.95)",
              textShadow: "0 2px 8px rgba(0,0,0,0.35)",
            }}
          >
            Scroll Down
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

interface HeroLayoutProps {
  activeSlide: number;
  onOpenInquiry: () => void;
  onScrollToGallery: () => void;
}

// Option A: editorial split layout (kept from previous design).
const HeroEditorialSplit: React.FC<HeroLayoutProps> = ({
  activeSlide,
  onOpenInquiry,
  onScrollToGallery,
}) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "none",
        mx: "auto",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.06fr 0.94fr" },
        gap: { xs: 3, md: 5 },
        alignItems: "stretch",
      }}
    >
      <motion.div
        initial={
          prefersReducedMotion ? undefined : { opacity: 0, y: 30, scale: 0.985 }
        }
        whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={
          prefersReducedMotion
            ? undefined
            : { duration: 0.68, ease: [0.22, 1, 0.36, 1] }
        }
      >
        <Box
          sx={{
            borderRadius: { xs: "24px", md: "32px" },
            p: { xs: 2.7, sm: 3.5, md: 5 },
            background:
              "linear-gradient(146deg, rgba(255,248,244,0.94), rgba(255,255,255,0.76))",
            border: "1px solid rgba(255,255,255,0.64)",
            boxShadow:
              "0 24px 54px rgba(26,10,14,0.24), inset 0 1px 0 rgba(255,255,255,0.82)",
            backdropFilter: "blur(6px)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
        <Box>
          <Typography
            component="p"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: "999px",
              background: "rgba(128, 51, 65, 0.12)",
              color: "#5f2732",
              fontFamily: '"Manrope", sans-serif',
              fontSize: { xs: "0.72rem", sm: "0.78rem" },
              fontWeight: 700,
              letterSpacing: "0.09em",
              textTransform: "uppercase",
              mb: 2,
              width: "fit-content",
            }}
          >
            Boutique Interior Studio
          </Typography>

          <Typography
            component="h1"
            sx={{
              fontFamily: '"Cormorant Garamond", serif',
              color: "#281518",
              fontWeight: 700,
              lineHeight: 1.04,
              letterSpacing: "0.01em",
              fontSize: { xs: "2.2rem", sm: "2.9rem", md: "4rem" },
              maxWidth: { md: "95%" },
            }}
          >
            Crafted Spaces That Elevate Everyday Living
          </Typography>

          <Typography
            sx={{
              mt: { xs: 1.4, md: 1.6 },
              color: "rgba(34,22,24,0.82)",
              fontFamily: '"Manrope", sans-serif',
              fontSize: { xs: "0.95rem", sm: "1rem", md: "1.08rem" },
              lineHeight: 1.75,
              maxWidth: "60ch",
            }}
          >
            We design timeless interiors for homes, offices, and boutique
            commercial spaces with end-to-end execution, transparent planning,
            and a detail-first approach.
          </Typography>
        </Box>

        <Box>
          <Box
            sx={{
              mt: { xs: 3, md: 3.8 },
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Button
              onClick={onOpenInquiry}
              endIcon={<ArrowOutwardRoundedIcon />}
              sx={{
                px: { xs: 2.2, sm: 2.8 },
                py: 1.15,
                borderRadius: "999px",
                background: "linear-gradient(138deg, #803341 0%, #a94e4f 100%)",
                color: "#fff",
                fontFamily: '"Manrope", sans-serif',
                fontWeight: 700,
                fontSize: { xs: "0.9rem", sm: "0.97rem" },
                textTransform: "none",
                boxShadow: "0 12px 24px rgba(128,51,65,0.34)",
                "&:hover": {
                  background:
                    "linear-gradient(138deg, #6d2835 0%, #944345 100%)",
                },
              }}
            >
              Book Consultation
            </Button>

            {SHOW_GALLERY && (
              <Button
                onClick={onScrollToGallery}
                sx={{
                  px: { xs: 2.2, sm: 2.8 },
                  py: 1.15,
                  borderRadius: "999px",
                  textTransform: "none",
                  border: "1px solid rgba(128,51,65,0.35)",
                  color: "#5d2530",
                  fontFamily: '"Manrope", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: "0.9rem", sm: "0.97rem" },
                  backdropFilter: "blur(2px)",
                  "&:hover": {
                    borderColor: "#803341",
                    background: "rgba(255,255,255,0.65)",
                  },
                }}
              >
                View Projects
              </Button>
            )}
          </Box>

          <Box
            sx={{
              mt: { xs: 2.5, md: 3.2 },
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1.1,
            }}
          >
            {heroMetrics.map((metric) => (
              <Box
                key={metric.label}
                sx={{
                  borderRadius: "14px",
                  p: { xs: 1.2, md: 1.35 },
                  background: "rgba(255,255,255,0.6)",
                  border: "1px solid rgba(128,51,65,0.15)",
                  minHeight: 74,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    fontWeight: 800,
                    color: "#3e1a21",
                    lineHeight: 1.2,
                  }}
                >
                  {metric.value}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.5,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    textTransform: "uppercase",
                    letterSpacing: "0.09em",
                    color: "rgba(62,26,33,0.76)",
                  }}
                >
                  {metric.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        </Box>
      </motion.div>

      <motion.div
        initial={
          prefersReducedMotion ? undefined : { opacity: 0, y: 38, scale: 0.98 }
        }
        whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={
          prefersReducedMotion
            ? undefined
            : { duration: 0.72, delay: 0.12, ease: [0.22, 1, 0.36, 1] }
        }
      >
        <Box
          sx={{
            position: "relative",
            minHeight: { xs: 320, sm: 420, md: "auto" },
            borderRadius: { xs: "24px", md: "30px" },
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.56)",
            boxShadow:
              "0 24px 56px rgba(20, 8, 10, 0.35), inset 0 0 0 1px rgba(255,255,255,0.4)",
            background: "rgba(255,255,255,0.16)",
          }}
        >
        {slidingImages.map((imageSrc, index) => (
          <Box
            key={`hero-preview-${imageSrc}`}
            component="img"
            src={imageSrc}
            alt={`Vista Fusion Studio interior project ${index + 1}`}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: activeSlide === index ? 1 : 0,
              transform: activeSlide === index ? "scale(1)" : "scale(1.03)",
              filter:
                activeSlide === index
                  ? "brightness(1.1) saturate(1.1)"
                  : "brightness(1) saturate(0.98)",
              transition:
                "opacity 1100ms ease-in-out, transform 1800ms ease-in-out, filter 700ms ease",
              userSelect: "none",
            }}
            loading={index === activeSlide ? "eager" : "lazy"}
            decoding="async"
            draggable={false}
            onContextMenu={(event) => event.preventDefault()}
          />
        ))}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(13,8,10,0.02) 0%, rgba(13,8,10,0.12) 42%, rgba(13,8,10,0.38) 100%)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            p: { xs: 2, md: 2.5 },
          }}
        >
          <Typography
            sx={{
              mt: 0.5,
              fontFamily: '"Cormorant Garamond", serif',
              color: "rgba(255,255,255,0.96)",
              fontSize: { xs: "1.55rem", md: "1.95rem" },
              fontWeight: 700,
              lineHeight: 1.12,
              maxWidth: "20ch",
            }}
          >
            Bespoke Interiors Crafted with Refined Detail
          </Typography>

        </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

// Option B: cinematic overlay layout (new alternate design).
const HeroCinematicOverlay: React.FC<HeroLayoutProps> = ({
  activeSlide,
  onOpenInquiry,
  onScrollToGallery,
}) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "none",
        mx: "auto",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: "calc(100vh - 80px)", md: "calc(100vh - 96px)" },
          height: { xs: "calc(100vh - 80px)", md: "calc(100vh - 96px)" },
          borderRadius: 0,
          overflow: "hidden",
          border: "none",
          boxShadow: "none",
        }}
      >
        {slidingImages.map((imageSrc, index) => (
          <Box
            key={`cinematic-slide-${imageSrc}`}
            component="img"
            src={imageSrc}
            alt={`Vista Fusion Studio showcase image ${index + 1}`}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: activeSlide === index ? 1 : 0,
              transform: activeSlide === index ? "scale(1)" : "scale(1.05)",
              filter:
                activeSlide === index
                  ? "brightness(1.12) saturate(1.12)"
                  : "brightness(1.02) saturate(1)",
              transition:
                "opacity 1150ms ease-in-out, transform 2100ms ease-in-out, filter 900ms ease",
              userSelect: "none",
            }}
            loading={index === activeSlide ? "eager" : "lazy"}
            decoding="async"
            draggable={false}
            onContextMenu={(event) => event.preventDefault()}
          />
        ))}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(112deg, rgba(11,8,9,0.42) 0%, rgba(11,8,9,0.24) 42%, rgba(11,8,9,0.3) 100%), radial-gradient(circle at 78% 86%, rgba(240,133,98,0.2), rgba(240,133,98,0) 44%)",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            minHeight: { xs: "calc(100vh - 80px)", md: "calc(100vh - 96px)" },
            height: { xs: "calc(100vh - 80px)", md: "calc(100vh - 96px)" },
            p: { xs: 2, sm: 2.6, md: 3.4 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: { xs: 1.9, md: 2.6 },
          }}
        >
          <motion.div
            initial={
              prefersReducedMotion ? undefined : { opacity: 0, y: 26, scale: 0.99 }
            }
            animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
            transition={
              prefersReducedMotion
                ? undefined
                : { duration: 0.68, ease: [0.22, 1, 0.36, 1] }
            }
          >
            <Box sx={{ maxWidth: { xs: "100%", md: "62%" } }}>
              <Typography
                component="p"
                sx={{
                  display: "inline-flex",
                  px: 1.4,
                  py: 0.45,
                  borderRadius: "999px",
                  border: "1px solid rgba(255,255,255,0.4)",
                  backgroundColor: "rgba(255,255,255,0.14)",
                  color: "rgba(255,255,255,0.94)",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: { xs: "0.72rem", sm: "0.76rem" },
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Signature Interior Experiences
              </Typography>

              <Typography
                component="h1"
                sx={{
                  mt: 1.3,
                  fontFamily: '"Cormorant Garamond", serif',
                  color: "#fff",
                  fontWeight: 700,
                  lineHeight: 1.04,
                  letterSpacing: "0.01em",
                  fontSize: { xs: "2.05rem", sm: "2.75rem", md: "4.05rem" },
                  maxWidth: "18ch",
                }}
              >
                A Distinctive Design Story for Every Space
              </Typography>

              <Typography
                sx={{
                  mt: 1.35,
                  color: "rgba(255,255,255,0.86)",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: { xs: "0.92rem", sm: "0.98rem", md: "1.08rem" },
                  lineHeight: 1.68,
                  maxWidth: "52ch",
                }}
              >
                From residential sanctuaries to high-performing commercial
                interiors, we deliver concept-to-completion execution with precise
                planning and material-driven craftsmanship.
              </Typography>

              <Box
                sx={{
                  mt: { xs: 2.2, md: 2.8 },
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.1,
                }}
              >
                <Button
                  onClick={onOpenInquiry}
                  endIcon={<ArrowOutwardRoundedIcon />}
                  sx={{
                    px: { xs: 2.1, sm: 2.8 },
                    py: 1.1,
                    borderRadius: "999px",
                    background:
                      "linear-gradient(135deg, #f47d71 0%, #d85f57 100%)",
                    color: "rgba(255,255,255,0.97)",
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 800,
                    textTransform: "none",
                    fontSize: { xs: "0.88rem", sm: "0.95rem" },
                    boxShadow: "0 12px 24px rgba(216,95,87,0.34)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #df6f64 0%, #c34f47 100%)",
                    },
                  }}
                >
                  Start Your Project
                </Button>

                {SHOW_GALLERY && (
                  <Button
                    onClick={onScrollToGallery}
                    sx={{
                      px: { xs: 2.1, sm: 2.8 },
                      py: 1.1,
                      borderRadius: "999px",
                      border: "1px solid rgba(255,255,255,0.46)",
                      color: "#fff",
                      fontFamily: '"Manrope", sans-serif',
                      fontWeight: 700,
                      fontSize: { xs: "0.88rem", sm: "0.95rem" },
                      textTransform: "none",
                      "&:hover": {
                        background: "rgba(255,255,255,0.12)",
                      },
                    }}
                  >
                    Explore Portfolio
                  </Button>
                )}
              </Box>
            </Box>
          </motion.div>

          <motion.div
            initial={
              prefersReducedMotion ? undefined : { opacity: 0, y: 32, scale: 0.99 }
            }
            animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
            transition={
              prefersReducedMotion
                ? undefined
                : { duration: 0.74, delay: 0.12, ease: [0.22, 1, 0.36, 1] }
            }
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                flexWrap: "wrap",
                gap: 1.35,
              }}
            >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))" },
                gap: 1,
                width: { xs: "100%", md: "auto" },
                minWidth: { md: 430 },
              }}
            >
              {heroMetrics.map((metric) => (
                <Box
                  key={`cinematic-${metric.label}`}
                  sx={{
                    p: { xs: 1.1, md: 1.3 },
                    borderRadius: "12px",
                    border: "1px solid rgba(255,255,255,0.24)",
                    background: "rgba(255,255,255,0.12)",
                    backdropFilter: "blur(3px)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Manrope", sans-serif',
                      color: "#fff",
                      fontSize: { xs: "1rem", md: "1.08rem" },
                      fontWeight: 800,
                      lineHeight: 1.2,
                    }}
                  >
                    {metric.value}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.35,
                      fontFamily: '"Manrope", sans-serif',
                      color: "rgba(255,255,255,0.82)",
                      fontSize: { xs: "0.66rem", sm: "0.72rem" },
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    {metric.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            </Box>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

export default Navbar;

interface SlidingImageHeroProps {
  activeSlide: number;
}

const SlidingImageHero: React.FC<SlidingImageHeroProps> = ({ activeSlide }) => {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: -1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {slidingImages.map((imageSrc, index) => (
        <Box
          key={`ambient-${imageSrc}`}
          component="img"
          src={imageSrc}
          alt=""
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: activeSlide === index ? 1 : 0,
            transform: activeSlide === index ? "scale(1.06)" : "scale(1.13)",
            filter: "blur(10px) brightness(1) saturate(1.04)",
            transition:
              "opacity 950ms ease-in-out, transform 1900ms ease-in-out",
            userSelect: "none",
          }}
          loading={index === activeSlide ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
        />
      ))}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(circle at 14% 16%, rgba(255,255,255,0.36), rgba(255,255,255,0) 45%), radial-gradient(circle at 82% 88%, rgba(255,205,171,0.32), rgba(255,205,171,0) 50%), linear-gradient(180deg, rgba(17,12,14,0.12) 0%, rgba(17,12,14,0.24) 100%)",
        }}
      />
    </Box>
  );
};
